import { FC, useContext, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  Button,
  ChangeRoleIcon,
  DashManIcon,
  ITabOption,
  Preloader,
  Tabs,
} from '@smartshell/ui-kit';
import { useNavigate } from 'react-router';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import {
  WORKERS_PAGE_INFO,
  IGetWorkersPageInfo,
  IGetRolesResponse,
  GET_ROLES,
} from 'schemas';

import RolesTable from './components/RolesTable';
import WorkersTable from './components/WorkersTable';
import { WorkerTabOpen } from './types';
import s from './Workers.module.scss';

export const Workers: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const { data: loadedWorkers, loading } = useQuery<IGetWorkersPageInfo>(
    WORKERS_PAGE_INFO,
    {
      fetchPolicy: 'no-cache',
      skip: !permissions?.has('worker_manage'),
    }
  );

  const { data: loadedRoles, loading: loadingRoles } =
    useQuery<IGetRolesResponse>(GET_ROLES, {
      fetchPolicy: 'no-cache',
      skip: !permissions?.has('role_browse'),
    });

  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<WorkerTabOpen>('workers');

  const {
    workers: { data: workersData, paginatorInfo },
  } = loadedWorkers ?? {
    workers: { data: [], paginatorInfo: null },
  };

  const { roles } = loadedRoles ?? {
    roles: [],
  };

  const radioTabOptions = useMemo<ITabOption<WorkerTabOpen>[]>(
    () => [
      {
        Icon: DashManIcon,
        value: 'workers',
        title: t('workers.tab.title'),
        count: workersData.length,
      },
      {
        Icon: ChangeRoleIcon,
        value: 'roles',
        title: t('roles.tab.title'),
        count: roles?.length ? roles?.length - 1 : 0,
      },
    ],
    [workersData, roles]
  );

  return (
    <Preloader loading={!permissions}>
      <div className={s['page-container']}>
        <div className={s.controls}>
          {permissions?.has('role_manage') && (
            <Tabs
              options={radioTabOptions}
              onChange={setActiveTab}
              value={activeTab}
              itemVariant="row"
              className={s.tabs}
            />
          )}
          {activeTab === 'workers' && permissions?.has('role_browse') && (
            <Button
              onClick={() => navigate('/workers/new')}
              iconName="CirclePlusIcon"
              disabled={
                !permissions?.has('worker_manage') ||
                !permissions?.has('client_info') ||
                loadingRoles
              }
            >
              {t('workers.add')}
            </Button>
          )}
        </div>
        <Preloader loading={loading || loadingRoles}>
          {activeTab === 'workers' && (
            <div className={s.workers}>
              <WorkersTable
                workers={workersData}
                paginatorInfo={paginatorInfo}
              />
            </div>
          )}
          {activeTab === 'roles' && permissions?.has('role_manage') && (
            <RolesTable roles={roles} />
          )}
        </Preloader>
      </div>
    </Preloader>
  );
};
